import { watch, type Ref } from 'vue';
import http from '@monolith/legacy/services/api/http';

const SUGGESTIONS_ENDPOINT = '/api/ankoragent/v1/brand/suggestions';

/**
 * Guards a second mount inside one document, nothing more.
 *
 * Every monolith page is a full document load, so this resets on each of them
 * and a brand moving around the back office warms once per page view.
 *
 * That is deliberate, and it replaced a `sessionStorage` guard that made it
 * once per tab session. `sessionStorage` has no TTL: a brand who never closes
 * the tab would never warm again, so their server-side set goes stale and they
 * pay the full generation wait the next time they open the assistant. The
 * endpoint returns early when a set is already pending or still fresh, so the
 * extra calls are cheap — staleness is the more expensive failure. The monolith
 * takes the worse end of that trade because it has no other option; the SPA
 * warms once per document too, which for a router-driven app is far fewer
 * calls.
 */
let warmed = false;

/**
 * Warms the brand's suggestion set when they land on a monolith back-office page.
 *
 * Generating suggestions is slow, so the backend starts it asynchronously the
 * first time they are asked for while the stored set is missing or stale
 * (SEL-178). Asking on arrival rather than when the assistant opens means the
 * set is usually ready by the time it is.
 *
 * `enabled` must be passed, not inferred from the calling component: the panel
 * this hangs off is mounted by the layout for every authenticated user and only
 * `v-if`s its own markup, so its setup runs for retailers and on pages outside
 * the back office too. Watched rather than read once because the flag depends on
 * the route and the account, which resolve after mount.
 *
 * Deliberately fire-and-forget: the response is discarded and a failure is a
 * debug line, nothing more.
 */
export const useWarmSuggestions = (enabled: Ref<boolean>): void => {
  watch(
    enabled,
    (isEnabled) => {
      if (!isEnabled || warmed) {
        return;
      }

      // Marked before the call, not after: two mounts in one document would
      // otherwise both fire while the first is still in flight.
      warmed = true;

      http()
        .get(SUGGESTIONS_ENDPOINT)
        .catch((error: unknown) => {
          // Dev-only, matching EventEmitter's debug helper: there is no logger
          // service here, and a warmer nobody is waiting on should not put
          // anything in a production console.
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.debug('[warm-suggestions] could not warm the suggestions', error);
          }
        });
    },
    { immediate: true }
  );
};
